import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import empty from "is-empty";
import Button from "src/components/common/Button";
import styles from "./styles.module.scss";
import TextBox from "src/components/common/TextBox";
import { ModalActions } from "src/redux/Modal/actions";
import { EMAIL_REGEX } from "src/utils";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { SettingsActions } from "src/redux/Settings/actions";
import DraggableFieldSelector from "src/components/common/DraggableFieldSelector";
import { CONFIRMATION_FIELDS } from "./confirmation_fields";

export default () => {
    const dispatch = useDispatch();
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);

    const emailsSettings = useSelector(SettingsSelectors.EmailsSettings);
    const {
        "enable-custom-confirmation-attachment":
            enabledCustomConfirmationAttachment,
    } = useSelector(SettingsSelectors.FeatureFlags);
    const confirmationFileFields = useSelector(
        SettingsSelectors.ConfirmationFileFields
    );

    const [borrowerEmails, setBorrowerEmails] = useState([
        ...emailsSettings.borrowerEmails,
    ]);
    const [lenderEmails, setLenderEmails] = useState([
        ...emailsSettings.lenderEmails,
    ]);
    const [borrowerError, setBorrowerError] = useState("");
    const [lenderError, setLenderError] = useState("");

    const scrollableRef = useRef(null);
    const [hasScrollbar, setHasScrollbar] = useState(false);

    // adds a listener to check if the scrollable element has a scrollbar
    useEffect(() => {
        const checkForScrollbar = () => {
            if (scrollableRef.current) {
                const element = scrollableRef.current;
                const hasVerticalScrollbar =
                    element.scrollHeight > element.clientHeight;
                setHasScrollbar(hasVerticalScrollbar);
            }
        };

        checkForScrollbar();

        const currentRef = scrollableRef.current;

        const resizeObserver = new ResizeObserver(() => {
            checkForScrollbar();
        });

        if (currentRef) {
            resizeObserver.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                resizeObserver.unobserve(currentRef);
            }
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        dispatch(
            SettingsActions.UpdateEmailSettings({
                borrowerEmails,
                lenderEmails,
            })
        );
    }, [borrowerEmails, lenderEmails, dispatch]);

    const verifyEmails = (emails) => {
        if (emails.some((email) => !EMAIL_REGEX.test(email))) {
            return "Invalid Email format";
        }
        return null;
    };

    const verifyEmail = (data) => {
        return verifyEmails([data]) === null;
    };

    return (
        <div className={styles.emailSettings}>
            <div
                ref={scrollableRef}
                className={styles.emailSettingsForm}
                style={{
                    height: enabledCustomConfirmationAttachment
                        ? "470px"
                        : "270px",
                    paddingRight: hasScrollbar ? "16px" : "0px",
                    transition: "padding-right 0.2s",
                }}
            >
                {enabledCustomConfirmationAttachment && (
                    <div
                        key="section-confirmation-fields"
                        className={styles.section}
                    >
                        <div className={styles.title}>
                            Fields included in record files
                        </div>
                        <div className={styles.description}>
                            Configure the fields to be populated in the CSV
                            attachment in the record emails.
                        </div>
                        <div className={styles.dndInput}>
                            <DraggableFieldSelector
                                selectedFields={confirmationFileFields}
                                selectableItems={CONFIRMATION_FIELDS}
                                onSelectionChanged={(fields) =>
                                    dispatch(
                                        SettingsActions.UpdateConfirmationFileFields(
                                            fields
                                        )
                                    )
                                }
                                dataTestId={"confirmation-file-fields"}
                            />
                        </div>
                    </div>
                )}
                <div key="section-borrower" className={styles.section}>
                    <div className={styles.title}>
                        Records as a Borrower
                    </div>
                    <div className={styles.description}>
                        Enter a list of email addresses to be notified on all
                        received records when borrowing.
                    </div>
                    <div className={styles.input}>
                        <TextBox
                            id="emails-borrower"
                            placeholder="Sample@kslnetwork.com"
                            data={empty(borrowerEmails) ? [] : borrowerEmails}
                            onChange={(value) => {
                                setBorrowerEmails([...value]);
                                setBorrowerError(verifyEmails(value));
                            }}
                            tabIndex={1}
                            verifyData={verifyEmail}
                            ariaLabel="Email borrower"
                        />
                    </div>
                    {!empty(borrowerError) && (
                        <div className={styles.error}>{borrowerError}</div>
                    )}
                </div>

                <div key="section-lender" className={styles.section}>
                    <div className={styles.title}>
                        Records as a Lender
                    </div>
                    <div className={styles.description}>
                        Enter a list of email addresses to be notified on all
                        sent records when lending.
                    </div>
                    <div className={styles.input}>
                        <TextBox
                            id="emails-lender"
                            placeholder="Sample@kslnetwork.com"
                            data={empty(lenderEmails) ? [] : lenderEmails}
                            onChange={(value) => {
                                setLenderEmails([...value]);
                                setLenderError(verifyEmails(value));
                            }}
                            tabIndex={2}
                            verifyData={verifyEmail}
                            ariaLabel="Email lender"
                        />
                    </div>
                    {!empty(lenderError) && (
                        <div className={styles.error}>{lenderError}</div>
                    )}
                </div>
            </div>
            <div key="footer" className={styles.footer}>
                <Button
                    onClick={() => dispatch(ModalActions.Close())}
                    value="Cancel"
                    color="reversedWhite"
                    dataTestId="cancel-email-settings"
                />
                <Button
                    value="Save"
                    onClick={() =>
                        dispatch(SettingsActions.SaveCPTYSettingsForEveryRole())
                    }
                    disabled={!isTraderRole || !empty(borrowerError) || !empty(lenderError)}
                    dataTestId="save-email-settings"
                />
            </div>
        </div>
    );
};
